/**
 * Build step 3 — requests (client and internal), escalations, client tasks (ADR-001 §11.5).
 *
 * Visibility: client requests, escalations and client tasks follow the client — managers all, an ops_member
 * their own clients (current SPOC) and items they own; requesters and HR none (404). Internal requests:
 * managers all, a requester ONLY their own, an ops member those assigned to them. Every write is audited
 * (real actor + acting-as). Proof bodies and checking rules are those of tasks (decision 12).
 */
import { IdempotencyHeaders, IfMatchHeaders, OptionalIfMatchHeaders } from './common.js';
import { Task } from './tasks.js';
import { ClientRequest, ClientRequestIdParams, ClientRequestPage, ClientTask, ClientTaskIdParams, ClientTaskPage, CloseInternalRequestBody, ConfirmBody, ConfirmResponse, CreateClientRequestBody, CreateClientTaskBody, CreateClientTaskResponse, CreateEscalationBody, CreateInternalRequestBody, DelayBody, DropClientTaskBody, EscalateBody, Escalation, EscalationIdParams, EscalationNoteBody, EscalationPage, InternalRequest, InternalRequestIdParams, InternalRequestOptions, InternalRequestPage, KeptBody, ListClientRequestsQuery, ListClientTasksQuery, ListEscalationsQuery, ListInternalRequestsQuery, ListNotificationsQuery, MarkReadBody, MyRequestsResponse, NotificationPage, PostClientRequestMessageBody, PostClientRequestMessageResponse, PostMessageBody, PostMessageResponse, RequestToTaskBody, TriageBody, TriageResponse, UpdateClientRequestBody, UpdateEscalationBody, ItemClosureParams, ItemCheckResponse, RequestFileParams, SignRequestUploadBody, } from './requests.js';
import { MediaUrlResponse, UploadSlot } from './meetings.js';
import { CheckClosureBody } from './tasks.js';
import { CloseTaskBody } from './tasks.js';
import { API_BASE, defineRoute } from './route.js';
const CLIENT_READ = ['clients.read.all', 'clients.read.own'];
const IR_READ = ['requests.triage', 'requests.read.own', 'tasks.read.own'];
const CLIENT_SCOPE = 'Managers; or the client’s current SPOC or the item’s owner. Anyone else: 404.';
const ACT = IdempotencyHeaders.extend(OptionalIfMatchHeaders.shape);
const ACTION = { idempotency: 'key', concurrency: 'if-match-optional', rateLimit: 'write' };
export const step3Routes = {
    // ============================================================ client requests
    'clientRequests.list': defineRoute({
        id: 'clientRequests.list',
        method: 'GET',
        path: `${API_BASE}/client-requests`,
        summary: 'Client requests with their response clock (most urgent first)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers → all; ops_member → own clients and own. Others: 403.' },
        query: ListClientRequestsQuery,
        success: { status: 200, description: 'A page of client requests', schema: ClientRequestPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clientRequests.get': defineRoute({
        id: 'clientRequests.get',
        method: 'GET',
        path: `${API_BASE}/client-requests/{id}`,
        summary: 'One client request, with its Conversation (oldest first)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: CLIENT_SCOPE },
        params: ClientRequestIdParams,
        success: { status: 200, description: 'The request', schema: ClientRequest },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clientRequests.create': defineRoute({
        id: 'clientRequests.create',
        method: 'POST',
        path: `${API_BASE}/client-requests`,
        summary: 'Log a client request (the response clock starts now)',
        description: 'Owner defaults to the client’s current SPOC; a different owner_id needs task.reassign (403). respond_by_at = ' +
            'received_at + the client response hours setting (24). Another member’s client → 404.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers, or the client’s SPOC. Others: 403/404.' },
        headers: IdempotencyHeaders,
        body: CreateClientRequestBody,
        success: { status: 201, description: 'Logged', schema: ClientRequest, headers: { Location: '/api/v1/client-requests/{id}' } },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'clientRequests.update': defineRoute({
        id: 'clientRequests.update',
        method: 'PATCH',
        path: `${API_BASE}/client-requests/{id}`,
        summary: 'Change status (not done), owner or text',
        description: 'Owner changes need task.reassign (403). Status done only through close (with proof).',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'The owner or a manager; the SPOC alone 403. Others: 404.' },
        params: ClientRequestIdParams,
        headers: IfMatchHeaders,
        body: UpdateClientRequestBody,
        success: { status: 200, description: 'Updated', schema: ClientRequest },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'clientRequests.close': defineRoute({
        id: 'clientRequests.close',
        method: 'POST',
        path: `${API_BASE}/client-requests/{id}/close`,
        summary: 'Close with proof (waits in the Review queue like a task)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'The owner or a manager. Others: 403/404.' },
        params: ClientRequestIdParams,
        headers: ACT,
        body: CloseTaskBody,
        success: { status: 200, description: 'Closed, closure not checked', schema: ClientRequest },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'clientRequests.escalate': defineRoute({
        id: 'clientRequests.escalate',
        method: 'POST',
        path: `${API_BASE}/client-requests/{id}/escalate`,
        summary: 'Raise an escalation from this request (linked both ways)',
        description: '409 when the request already has an open escalation.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'The owner or a manager. Others: 403/404.' },
        params: ClientRequestIdParams,
        headers: IdempotencyHeaders,
        body: EscalateBody,
        success: { status: 201, description: 'The escalation', schema: Escalation },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'clientRequests.toTask': defineRoute({
        id: 'clientRequests.toTask',
        method: 'POST',
        path: `${API_BASE}/client-requests/{id}/task`,
        summary: 'Make a task from this request',
        description: 'Default due: today when 8 hours or less are left, else tomorrow; priority urgent when breached, high ' +
            'when ≤ 8 h left, else medium. Assignee defaults to the request owner; picking someone else needs task.reassign.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'The owner or a manager. Others: 403/404.' },
        params: ClientRequestIdParams,
        headers: IdempotencyHeaders,
        body: RequestToTaskBody,
        success: { status: 201, description: 'The task (source request, origin_ref → the request)', schema: Task },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'clientRequests.message': defineRoute({
        id: 'clientRequests.message',
        method: 'POST',
        path: `${API_BASE}/client-requests/{id}/messages`,
        summary: '“Send message” — post to the request’s Conversation (Ops-only; never moves the response clock)',
        description: 'A finished (done) request takes no more messages: 409.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: CLIENT_SCOPE },
        params: ClientRequestIdParams,
        headers: IdempotencyHeaders,
        body: PostClientRequestMessageBody,
        success: { status: 201, description: 'The message and the request after it (with its Conversation)', schema: PostClientRequestMessageResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ internal requests
    'internalRequests.list': defineRoute({
        id: 'internalRequests.list',
        method: 'GET',
        path: `${API_BASE}/internal-requests`,
        summary: 'Internal requests (Sales / Marketing → Ops)',
        access: {
            auth: 'session',
            anyOf: IR_READ,
            note: 'Managers → all; a requester → ONLY their own; an ops member → those assigned to them. HR: 403.',
        },
        query: ListInternalRequestsQuery,
        success: { status: 200, description: 'A page of requests, newest first', schema: InternalRequestPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'internalRequests.options': defineRoute({
        id: 'internalRequests.options',
        method: 'GET',
        path: `${API_BASE}/internal-requests/options`,
        summary: 'Choices for the request form: clients (names only), Ops people, types, purposes, needed-by',
        access: { auth: 'session', anyOf: ['requests.raise', 'requests.triage'], note: 'Requesters and managers. Others: 403.' },
        success: { status: 200, description: 'The choices', schema: InternalRequestOptions },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'internalRequests.get': defineRoute({
        id: 'internalRequests.get',
        method: 'GET',
        path: `${API_BASE}/internal-requests/{id}`,
        summary: 'One request with its thread (internal notes omitted for requesters)',
        access: { auth: 'session', anyOf: IR_READ, note: 'The requester who raised it, its owner, or a manager. Others: 404.' },
        params: InternalRequestIdParams,
        success: { status: 200, description: 'The request with messages', schema: InternalRequest },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'internalRequests.create': defineRoute({
        id: 'internalRequests.create',
        method: 'POST',
        path: `${API_BASE}/internal-requests`,
        summary: 'Ask Ops for help (purpose, needed-by, why it matters)',
        description: 'Sales and marketing raise their own; a manager may log one on a requester’s behalf (on_behalf_of_id must ' +
            'be an active sales/marketing user, 422). ack_by_at = now + 24 h. file_keys: finished uploads the caller made ' +
            'with POST /internal-requests/sign-upload (422 otherwise).',
        access: { auth: 'session', anyOf: ['requests.raise', 'requests.triage'], note: 'Sales, marketing; managers on behalf. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateInternalRequestBody,
        success: { status: 201, description: 'Sent to Ops', schema: InternalRequest, headers: { Location: '/api/v1/internal-requests/{id}' } },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'internalRequests.signUpload': defineRoute({
        id: 'internalRequests.signUpload',
        method: 'POST',
        path: `${API_BASE}/internal-requests/sign-upload`,
        summary: 'Get an upload slot for a file to attach to a request ("Attach files")',
        description: 'The browser PUTs the file straight to storage, then sends the key in file_keys on POST /internal-requests. ' +
            'The key is bound to the caller; an unused one is never attached to anything.' +
            ' 429 rate_limited (Postgres file store only) when the caller’s, or everyone’s, uploads not yet attached to anything are at their cap (FILES_PENDING_USER_MB / FILES_PENDING_TOTAL_MB), or the caller’s link uploads of the last 24 hours, attached or not, are at their day cap (FILES_UPLOAD_USER_DAY_MB); uploads nothing uses are deleted after 24 hours.',
        access: { auth: 'session', anyOf: ['requests.raise', 'requests.triage'], note: 'Sales, marketing; managers (logging on behalf). Others: 403. Not while viewing as someone (403).' },
        headers: IdempotencyHeaders,
        body: SignRequestUploadBody,
        success: { status: 201, description: 'Upload slot', schema: UploadSlot },
        errors: ['no_session', 'forbidden', 'validation_failed', 'rate_limited', 'upstream_unavailable'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'internalRequests.fileUrl': defineRoute({
        id: 'internalRequests.fileUrl',
        method: 'GET',
        path: `${API_BASE}/internal-requests/{id}/files/{n}`,
        summary: 'Open an attached file: a short-lived link',
        description: '`n` is the file’s position in the request’s `files` (0-based). The link is same-origin (`/api/v1/files/{token}`, see `files.get`) and works only for the signed-in person it was issued to. 404 when there is no such file.',
        access: { auth: 'session', anyOf: IR_READ, note: 'Whoever can read the request: the requester, its owner, or a manager. Others: 404.' },
        params: RequestFileParams,
        success: { status: 200, description: 'Same-origin file link (expires in 10 minutes)', schema: MediaUrlResponse },
        errors: ['no_session', 'not_found', 'upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'internalRequests.triage': defineRoute({
        id: 'internalRequests.triage',
        method: 'POST',
        path: `${API_BASE}/internal-requests/{id}/triage`,
        summary: 'Triage: accept (owner + expected date, creates a task), ask for details (pauses clock), or decline',
        access: { auth: 'session', anyOf: IR_READ, action: ['requests.triage'], note: 'Managers. Others who can see it: 403; else 404.' },
        params: InternalRequestIdParams,
        headers: IdempotencyHeaders,
        body: TriageBody,
        success: { status: 200, description: 'The request, and the task when accepted', schema: TriageResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'internalRequests.message': defineRoute({
        id: 'internalRequests.message',
        method: 'POST',
        path: `${API_BASE}/internal-requests/{id}/messages`,
        summary: 'Post to the thread (a requester reply from needs-details resumes the clock)',
        description: 'is_internal_note is for Ops only: a requester sending it gets 403.',
        access: { auth: 'session', anyOf: IR_READ, note: 'The requester (own), the owner, or a manager. Others: 404.' },
        params: InternalRequestIdParams,
        headers: IdempotencyHeaders,
        body: PostMessageBody,
        success: { status: 201, description: 'The message and the request after it', schema: PostMessageResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'internalRequests.delay': defineRoute({
        id: 'internalRequests.delay',
        method: 'POST',
        path: `${API_BASE}/internal-requests/{id}/delay`,
        summary: '“Running late?” — new expected date and why (requester notified)',
        access: { auth: 'session', anyOf: IR_READ, note: 'The owner or a manager; the requester 403. Others: 404.' },
        params: InternalRequestIdParams,
        headers: ACT,
        body: DelayBody,
        success: { status: 200, description: 'The request', schema: InternalRequest },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'internalRequests.close': defineRoute({
        id: 'internalRequests.close',
        method: 'POST',
        path: `${API_BASE}/internal-requests/{id}/close`,
        summary: 'Close with proof and a note → waiting for the requester’s confirmation',
        access: { auth: 'session', anyOf: IR_READ, note: 'The owner or a manager; the requester 403. Others: 404.' },
        params: InternalRequestIdParams,
        headers: ACT,
        body: CloseInternalRequestBody,
        success: { status: 200, description: 'The request (waiting_confirm)', schema: InternalRequest },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'internalRequests.confirm': defineRoute({
        id: 'internalRequests.confirm',
        method: 'POST',
        path: `${API_BASE}/internal-requests/{id}/confirm`,
        summary: 'The requester confirms it is done — or says “Not quite yet”',
        description: 'Only the requester who raised it (others who can see it: 403). Only from waiting_confirm (409).',
        access: { auth: 'session', anyOf: IR_READ, note: 'The requester who raised it. Others: 403/404.' },
        params: InternalRequestIdParams,
        headers: IdempotencyHeaders,
        body: ConfirmBody,
        success: { status: 200, description: 'The request, and the reopened task on not_yet', schema: ConfirmResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'me.requests': defineRoute({
        id: 'me.requests',
        method: 'GET',
        path: `${API_BASE}/me/requests`,
        summary: 'My requests (requester home): own requests plus notifications',
        access: { auth: 'session', anyOf: ['requests.read.own'], note: 'Sales and marketing. Others: 403.' },
        success: { status: 200, description: 'Own requests and notifications', schema: MyRequestsResponse },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'notifications.list': defineRoute({
        id: 'notifications.list',
        method: 'GET',
        path: `${API_BASE}/notifications`,
        summary: 'My in-app notifications, newest first',
        access: { auth: 'session', note: 'Any signed-in user; only their own.' },
        query: ListNotificationsQuery,
        success: { status: 200, description: 'A page of notifications', schema: NotificationPage },
        errors: ['no_session', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'notifications.read': defineRoute({
        id: 'notifications.read',
        method: 'POST',
        path: `${API_BASE}/notifications/read`,
        summary: 'Mark notifications read',
        description: 'Idempotent; ids that are not yours are ignored.',
        access: { auth: 'session', note: 'Any signed-in user; only their own.' },
        body: MarkReadBody,
        success: { status: 204, description: 'Marked' },
        errors: ['no_session', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ escalations
    'escalations.list': defineRoute({
        id: 'escalations.list',
        method: 'GET',
        path: `${API_BASE}/escalations`,
        summary: 'Escalations (open high ones first)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers → all; ops_member → own clients and owned. Others: 403.' },
        query: ListEscalationsQuery,
        success: { status: 200, description: 'A page of escalations', schema: EscalationPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'escalations.get': defineRoute({
        id: 'escalations.get',
        method: 'GET',
        path: `${API_BASE}/escalations/{id}`,
        summary: 'One escalation with its dated log',
        access: { auth: 'session', anyOf: CLIENT_READ, note: CLIENT_SCOPE },
        params: EscalationIdParams,
        success: { status: 200, description: 'The escalation', schema: Escalation },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'escalations.create': defineRoute({
        id: 'escalations.create',
        method: 'POST',
        path: `${API_BASE}/escalations`,
        summary: 'Raise an escalation (always by a person)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers, or the client’s SPOC. Others: 403/404.' },
        headers: IdempotencyHeaders,
        body: CreateEscalationBody,
        success: { status: 201, description: 'Raised', schema: Escalation, headers: { Location: '/api/v1/escalations/{id}' } },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'escalations.update': defineRoute({
        id: 'escalations.update',
        method: 'PATCH',
        path: `${API_BASE}/escalations/{id}`,
        summary: 'Change severity, title or owner',
        description: 'Owner changes need task.reassign (403). 409 when closed.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'The owner or a manager; the SPOC alone 403. Others: 404.' },
        params: EscalationIdParams,
        headers: IfMatchHeaders,
        body: UpdateEscalationBody,
        success: { status: 200, description: 'Updated', schema: Escalation },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'escalations.note': defineRoute({
        id: 'escalations.note',
        method: 'POST',
        path: `${API_BASE}/escalations/{id}/notes`,
        summary: 'Add a dated note to the log',
        access: { auth: 'session', anyOf: CLIENT_READ, note: CLIENT_SCOPE },
        params: EscalationIdParams,
        headers: IdempotencyHeaders,
        body: EscalationNoteBody,
        success: { status: 201, description: 'The escalation with the new note', schema: Escalation },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'escalations.close': defineRoute({
        id: 'escalations.close',
        method: 'POST',
        path: `${API_BASE}/escalations/{id}/close`,
        summary: 'Close with proof',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'The owner or a manager. Others: 403/404.' },
        params: EscalationIdParams,
        headers: ACT,
        body: CloseTaskBody,
        success: { status: 200, description: 'Closed', schema: Escalation },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'closures.checkItem': defineRoute({
        id: 'closures.checkItem',
        method: 'POST',
        path: `${API_BASE}/closures/{item_kind}/{item_id}/check`,
        summary: 'Check proof on a closed client request or escalation: “Looks right” or “Send back”',
        description: 'Same rule as tasks (operator decision 12): never the closer; the associate director checks, the founder ' +
            'checks the AD’s. send_back reopens the item and records a closure incident. 409 when not waiting.',
        access: {
            auth: 'session',
            anyOf: CLIENT_READ,
            action: ['proof.check', 'proof.check.ad_closures'],
            note: 'Ops associate director; founder for the AD’s closures. Others who can see it: 403; else 404.',
        },
        params: ItemClosureParams,
        headers: IdempotencyHeaders,
        body: CheckClosureBody,
        success: { status: 200, description: 'The closure after the check', schema: ItemCheckResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ client tasks
    'clientTasks.list': defineRoute({
        id: 'clientTasks.list',
        method: 'GET',
        path: `${API_BASE}/client-tasks`,
        summary: 'Client tasks — what we told clients we would do (Work → Client tasks)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers → all; ops_member → own clients and own. Others: 403.' },
        query: ListClientTasksQuery,
        success: { status: 200, description: 'A page, overdue first', schema: ClientTaskPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clientTasks.get': defineRoute({
        id: 'clientTasks.get',
        method: 'GET',
        path: `${API_BASE}/client-tasks/{id}`,
        summary: 'One client task',
        access: { auth: 'session', anyOf: CLIENT_READ, note: CLIENT_SCOPE },
        params: ClientTaskIdParams,
        success: { status: 200, description: 'The client task', schema: ClientTask },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clientTasks.create': defineRoute({
        id: 'clientTasks.create',
        method: 'POST',
        path: `${API_BASE}/client-tasks`,
        summary: 'Record what we told the client we would do (creates a linked high-priority task)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers, or the client’s SPOC. Others: 403/404.' },
        headers: IdempotencyHeaders,
        body: CreateClientTaskBody,
        success: { status: 201, description: 'The client task and its linked task', schema: CreateClientTaskResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'clientTasks.kept': defineRoute({
        id: 'clientTasks.kept',
        method: 'POST',
        path: `${API_BASE}/client-tasks/{id}/kept`,
        summary: 'Mark kept, with proof (also closes the linked task)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Who said it, the linked task’s assignee, or a manager. Others: 403/404.' },
        params: ClientTaskIdParams,
        headers: ACT,
        body: KeptBody,
        success: { status: 200, description: 'Kept', schema: ClientTask },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'clientTasks.drop': defineRoute({
        id: 'clientTasks.drop',
        method: 'POST',
        path: `${API_BASE}/client-tasks/{id}/drop`,
        summary: 'Drop with a reason (also drops the linked task; lands in Dropped tasks)',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'A manager, or who said it. Others: 403/404.' },
        params: ClientTaskIdParams,
        headers: ACT,
        body: DropClientTaskBody,
        success: { status: 200, description: 'Dropped', schema: ClientTask },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
};
//# sourceMappingURL=routes.step3.js.map